/**
 * How code outside React tells the app its session may have ended.
 *
 * ## The gap this closes
 *
 * Two clients reach the API. `createAuthClient` handles `/auth/*` and already treats a 401 on an
 * authed request as *"the session ended"*. Every domain call — settings, events, registrants —
 * goes through `@h2hspark/ui`'s `createApiRequest`, which threw and did nothing else.
 *
 * So an expired session behaved differently depending on which client noticed first. Navigating
 * the console after expiry left every page on its loading skeletons: the domain calls were
 * 401ing, `useAuthMe`'s cached user was still non-null, and nothing re-checked. Only a browser
 * refresh re-ran `/auth/me`, which 401'd through the *auth* client and redirected. An organizer
 * clicking through a stale tab saw an application that had simply stopped answering.
 *
 * ## Why a re-check rather than a redirect
 *
 * A 401 on one domain call is **evidence** the session may be over, not proof. `/auth/me` stays
 * the authority: if the session really has ended it answers 401, `useAuthMe` clears the user, and
 * the app's existing redirector sends the browser to sign-in carrying `next`. If it answers
 * normally — the 401 was about that one resource — nothing happens, and the user is not thrown
 * out of a session that was working.
 *
 * That also means this file adds no redirect of its own. It reuses the chain that already exists
 * and was only ever missing its second trigger.
 */
const listeners = new Set();
/**
 * A request that arrived before anybody was listening, held until somebody is.
 *
 * **This is not a nicety; a dropped request is the whole failure this file exists to fix.** The
 * provider subscribes from a `useEffect`, which React runs after the first paint. A domain call
 * made in a child's effect can therefore be *refused* before that subscription exists — the
 * refusal is announced to an empty set and nothing ever asks `/auth/me`. CI caught it on
 * 2026-09-02: the same test that passes locally sat for 87 seconds on a console that never
 * redirected, because the runner is slow enough to lose that race and a developer's machine is
 * not.
 *
 * Latched rather than queued, and cleared on delivery: the request carries no information beyond
 * "something doubted the session", so two of them are one, and re-delivering it to a second
 * subscriber would re-check a session the first has already settled.
 */
let requestedBeforeAnybodyListened = false;
/**
 * Subscribe to re-check requests. Returns an unsubscribe function.
 *
 * Called by `AuthProvider`; there is no reason for an application to call it.
 */
export function onSessionRecheckRequested(listener) {
    listeners.add(listener);
    if (requestedBeforeAnybodyListened) {
        requestedBeforeAnybodyListened = false;
        listener();
    }
    return () => {
        listeners.delete(listener);
    };
}
/**
 * Ask the app to re-verify its session.
 *
 * Safe to call from anywhere, including outside React and before any provider has mounted. With no
 * listener the request is **held**, not dropped, and delivered when one subscribes — see
 * {@link requestedBeforeAnybodyListened}. A page that has no session to re-check answers it with
 * nothing, which is the provider's early return on a null token rather than a silence here.
 */
export function requestSessionRecheck() {
    if (listeners.size === 0) {
        requestedBeforeAnybodyListened = true;
        return;
    }
    for (const listener of listeners)
        listener();
}
//# sourceMappingURL=session-recheck.js.map